import { BydModelInterface } from '../interfaces/byd-model.interface';

/**
 * Creates an instance of a model it also defines a methods for manipulate the instances,
 * used as type in services methods,
 * holds models state that may change over time.
 */
export class BydModel<T> {
  /**
   * An array of instances type of `T`, is stored in this variable.
   */
  public models: T[] = [];

  /**
   * Contains the models at a particular moment in time.
   */
  public modelsSnapshot: T[] = [];

  /**
   * Contains the models instances total.
   */
  public totalModels: number | null = null;

  /**
   * Contains the number of elements per page
   */
  public numPerPage: number | null = null;

  /**
   * Contains the models instances total.
   */
  public totalPages?: number | null = null;

  /**
   * The current page number.
   */
  public pageNumber?: number | null = null;

  /**
   * Creates an instance of Beyond Model with type of `T`.
   *
   * @param jsonData the generic model
   */
  constructor(jsonData: BydModelInterface<T>) {
    this.models = jsonData.models?.filter((model) => model);
    this.modelsSnapshot = jsonData.models;
    this.totalModels = jsonData.totalModels;
    this.numPerPage = jsonData.numPerPage;
    this.totalPages = jsonData.totalPages;
    this.pageNumber = jsonData.pageNumber;
  }

  /**
   * Set the models.
   *
   * @param models The array of a specified instances.
   * @returns A Model type of `T`.
   */
  public setModels(models: T[]): BydModel<T> {
    this.models = models;
    return this;
  }

  /**
   * Gets the length of the models array.
   *
   * @returns the models length.
   */
  get lengthModels(): number {
    return this.models.length;
  }

  /**
   * Removes the first element from the models.
   *
   * @returns models.
   */
  public shiftModels() {
    return this.models.shift();
  }

  /**
   * Removes the last element from the models.
   *
   * @returns models.
   */
  public popModels() {
    return this.models.pop();
  }

  /**
   * Appends new model to models.
   *
   * @param model — The new model.
   * @returns models.
   */
  public pushModels(model: T) {
    return this.models.push(model);
  }

  /**
   * @param start — The beginning of the specified portion of the models array.
   * @param end — The end of the specified portion of the models array.
   * @returns models.
   */
  public sliceModels(start?: number, end?: number) {
    return this.models.slice(start, end);
  }

  /**
   * Combines two or more models arrays.
   *
   * @param items — Additional items to add to the end of models array.
   * @returns concatModels
   */
  public concatModels(items: ConcatArray<T>) {
    this.models = this.models.concat(items);
    return this;
  }

  /**
   * Removes elements from the models.
   *
   * @param start — The zero-based location in the models array from which to start removing elements.
   * @param deleteCount — The number of elements to remove from models.
   * @returns models.
   */
  public spliceModels(start: number, deleteCount?: number) {
    return this.models.splice(start, deleteCount);
  }

  /**
   * Get the index of the model.
   *
   * @param  model The model to locate in the models.
   * @returns the index.
   */
  public indexOfModel(model: T) {
    return this.models.indexOf(model);
  }

  /**
   * Returns the value of the first element in the models where predicate is true, and undefined otherwise.
   *
   * @param callbackfn — The callbackfn function will be called one time for each element in the models.
   * @returns that model value. Otherwise, findModel returns undefined.
   */
  // TODO:  Set up the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public findModel(callbackfn: any): T | undefined {
    return this.models.find(callbackfn);
  }

  /**
   * Removes model from the models.
   *
   * @param model The model to remove.
   * @returns The Model type of `T`.
   */
  public removeModel(model: T): BydModel<T> {
    this.models.splice(this.indexOfModel(model), 1);
    return this;
  }

  /**
   * Performs the specified action for each element in models.
   *
   * @param callback The map method calls the callbackfn function one time for each element in the models.
   */
  // TODO:  Set up the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public forEachModels(callback: any) {
    this.models.forEach(callback);
  }

  /**
   * Get the index of the first element in the models where predicate is true, and -1 otherwise.
   *
   * @param callbackfn The map method calls the callbackfn function one time for each element in the models.
   * @returns the index of the searched model.
   */
  // TODO:  Set up the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public findIndexModels(callbackfn: any) {
    return this.models.findIndex(callbackfn);
  }

  /**
   * Calls a defined callback function on each element of models.
   *
   * @param callbackfn The map method calls the callbackfn function one time for each element in the models.
   * @returns an array that contains the models.
   */
  // TODO:  Set up the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public mapModels(callbackfn: any) {
    return this.models.map(callbackfn);
  }
}
